// Inspired by
// https://github.com/lunametrics/youtube-google-analytics
//
// Original implementation by LunaMetrics

/** Internal dependencies */
import { NCST } from '../';

export default class YouTubeTrack {
	/*
	 * Configuration Details
	 *
	 * @property events object
	 * Defines which events emitted by YouTube API
	 * will be turned into Google Analytics or GTM events
	 *
	 * @property percentageTracking object
	 * Object with configurations for percentage viewed events
	 *
	 *   @property each array
	 *   Fires an event once each percentage ahs been reached
	 *
	 *   @property every number
	 *   Fires an event for every n% viewed
	 */
	constructor( player, config = {} ) {
		this.player = player;
		this.playTracker = {};
		this.pauseFlag = false;
		this.heartbeat = false;

		this.config = {
			events: {
				Play: true,
				Pause: true,
				'Watch to End': true,
			},
			percentageTracking: {
				every: 25,
			},
			...config,
		};

		player.addEventListener(
			'onStateChange',
			this.onStateChangeHandler.bind( this )
		);
	}

	// Returns key/value pairs of percentages: number of seconds to achieve
	getMarks( duration ) {
		var marks = {};

		// For full support, we're handling Watch to End with percentage viewed
		if ( this.config.events[ 'Watch to End' ] ) {
			marks[ 'Watch to End' ] = ( duration * 99 ) / 100;
		}

		if ( this.config.percentageTracking ) {
			var points = [];
			var i;

			if ( this.config.percentageTracking.each ) {
				points = points.concat( this.config.percentageTracking.each );
			}

			if ( this.config.percentageTracking.every ) {
				var every = parseInt(
					this.config.percentageTracking.every,
					10
				);
				var num = 100 / every;

				for ( i = 1; i < num; i++ ) {
					points.push( i * every );
				}
			}

			for ( i = 0; i < points.length; i++ ) {
				var _point = points[ i ];
				var _mark = _point + '%';
				var _time = ( duration * _point ) / 100;

				marks[ _mark ] = Math.floor( _time );
			}
		}

		return marks;
	}

	checkCompletion( player, marks, videoId ) {
		var currentTime = player.getCurrentTime();
		var key;

		for ( key in marks ) {
			if (
				marks[ key ] <= currentTime &&
				! this.playTracker[ videoId ][ key ]
			) {
				this.playTracker[ videoId ][ key ] = true;
				this.fireAnalyticsEvent( videoId, key );
			}
		}
	}

	// Event handler for events emitted from the YouTube API
	onStateChangeHandler( event ) {
		var self = this;
		var stateIndex = event.data;
		var player = event.target;
		var targetVideoUrl = player.getVideoUrl();
		var targetVideoId = player.getVideoData().video_id;
		var duration = player.getDuration();
		var marks = self.getMarks( duration );
		var playerStatesIndex = {
			'1': 'Play',
			'2': 'Pause',
		};
		var state = playerStatesIndex[ stateIndex ];

		// Unstarted
		if ( stateIndex === -1 ) {
			self.playTracker[ targetVideoId ] = {};
		}

		// Set up interval for completion checks
		if ( stateIndex === YT.PlayerState.PLAYING && ! self.heartbeat ) {
			clearInterval( self.heartbeat );

			self.heartbeat = setInterval( function () {
				// Check every second to see if we've hit any of our percentage viewed marks
				self.checkCompletion( player, marks, targetVideoId );
			}, 1000 );
		} else {
			clearInterval( self.heartbeat );
			self.heartbeat = false;
		}

		// No longer paused
		if ( stateIndex === YT.PlayerState.PLAYING ) {
			self.pauseFlag = false;
		}

		if ( typeof self.playTracker[ targetVideoId ] === 'undefined' ) {
			// This video hasn't started yet, so this is spam
			return false;
		}

		if ( stateIndex === YT.PlayerState.PAUSED ) {
			if ( ! self.pauseFlag ) {
				self.pauseFlag = true;
			} else {
				// We don't want to fire consecutive pause events
				return false;
			}
		}

		// If we're meant to track this event, fire it
		if ( this.config.events[ state ] ) {
			this.fireAnalyticsEvent( targetVideoId, state );
		}
	}

	// Fire an event to Google Tag Manager
	fireAnalyticsEvent( videoId, state ) {
		var videoUrl = 'https://www.youtube.com/watch?v=' + videoId;
		NCST.controllers.analytics.broadcastEvent( 'ncst.vidTrack', {
			category: 'Videos',
			action: state,
			label: videoUrl,
		} );
	}
}
